import { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { usePanelSettled } from '@/context/PanelSettledContext';

interface ScripturalConnectionLinesProps {
    containerRef: React.RefObject<HTMLDivElement>;
    isVisible: boolean;
    connectionPoints: Array<{ id: string; ref: string; percent: number }> | null;
    passageId?: string;
    isTransitionAnimating?: boolean;
}

interface ConnectionLine {
    id: string;
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    color: string;
    opacity: number;
}

export function ScripturalConnectionLines({
    containerRef,
    isVisible,
    connectionPoints,
    passageId,
    isTransitionAnimating
}: ScripturalConnectionLinesProps) {
    const [lines, setLines] = useState<ConnectionLine[]>([]);
    const requestRef = useRef<number>(undefined);

    const updateLines = useCallback(() => {
        if (!isVisible || !containerRef.current || !connectionPoints) return;

        const container = containerRef.current;

        // Force a reflow to ensure layout is settled before measuring
        void container.offsetHeight;

        const containerRect = container.getBoundingClientRect();

        const newLines: ConnectionLine[] = [];

        // Find both allusion and crossref cards
        const cards = container.querySelectorAll('.allusion-card[data-passage-ref], .crossref-card');

        cards.forEach((card, index) => {
            const cardRect = card.getBoundingClientRect();
            const itemId = card.id;
            if (!itemId) return;

            // Find the corresponding marker on the bar
            const marker = document.querySelector(`[data-marker-id="${itemId}"]`);
            if (!marker) return;

            const markerRect = marker.getBoundingClientRect();

            // X and Y positions are relative to the container
            const targetX = (markerRect.left + markerRect.width / 2) - containerRect.left;
            const targetY = (markerRect.top + markerRect.height / 2) - containerRect.top;

            // Find the corresponding marker on the card spine
            const cardAnchor = card.querySelector(`[data-card-anchor]`);
            const anchorRect = cardAnchor ? cardAnchor.getBoundingClientRect() : cardRect;

            const cardX = (anchorRect.left + anchorRect.width / 2) - containerRect.left;
            const cardY = (anchorRect.top + anchorRect.height / 2) - containerRect.top;

            // Cross-refs use lower opacity to be more subtle
            const isCrossRef = card.classList.contains('crossref-card');
            const opacity = isCrossRef ? 0.2 : 0.35;

            newLines.push({
                id: `${passageId}-line-${itemId}-${index}`,
                startX: cardX,
                startY: cardY,
                endX: targetX,
                endY: targetY,
                color: 'currentColor',
                opacity
            });
        });

        setLines(newLines);
    }, [isVisible, containerRef, connectionPoints, passageId]);

    const { waitForSettled, isSettled } = usePanelSettled();

    // Recalculate when connection points change (after panel settles)
    useEffect(() => {
        // Clear lines immediately to avoid showing stale positions during animations
        setLines([]);

        if (!isVisible || !connectionPoints) {
            return;
        }

        let cancelled = false;

        const measureAfterSettled = async () => {
            // Wait for the panel animation to complete
            await waitForSettled();

            if (cancelled) return;

            // Extra frame to ensure layout is fully painted
            await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

            if (cancelled) return;

            updateLines();
        };

        measureAfterSettled();

        return () => {
            cancelled = true;
        };
    }, [isVisible, connectionPoints, updateLines, waitForSettled]);

    // Extra update when transition ends or panel settles as a safety net
    useEffect(() => {
        if (isSettled && !isTransitionAnimating && isVisible && connectionPoints) {
            // Quick follow-up measurement in case anything shifted
            requestAnimationFrame(() => {
                updateLines();
            });
        }
    }, [isTransitionAnimating, isSettled, isVisible, connectionPoints, updateLines]);

    // Handle scroll and resize updates
    useLayoutEffect(() => {
        if (!isVisible) return;

        const handleUpdate = () => {
            if (requestRef.current) return;
            requestRef.current = requestAnimationFrame(() => {
                updateLines();
                requestRef.current = undefined;
            });
        };

        const scrollContainer = containerRef.current?.closest('[data-radix-scroll-area-viewport]') || window;

        scrollContainer.addEventListener('scroll', handleUpdate, true);
        window.addEventListener('resize', handleUpdate);

        return () => {
            scrollContainer.removeEventListener('scroll', handleUpdate, true);
            window.removeEventListener('resize', handleUpdate);
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
        };
    }, [isVisible, updateLines, containerRef]);

    return (
        <div className="absolute inset-0 pointer-events-none z-[100] overflow-hidden" style={{ height: '100%' }}>
            <svg width="100%" height="100%" className="text-primary/40">
                {lines.map((line) => (
                    <path
                        key={line.id}
                        d={`M ${line.startX} ${line.startY} 
                           C ${line.startX} ${line.startY - 50}, 
                             ${line.endX} ${line.endY + 50}, 
                             ${line.endX} ${line.endY}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeDasharray="4 4"
                        opacity={line.opacity}
                    />
                ))}
            </svg>
        </div>
    );
}
